/**
 * UnifiedCalendar Component
 * V226: 통합 캘린더 컴포넌트
 *
 * Features:
 * - 단일/범위/다중 선택 모드
 * - 한국어 요일 (일~토)
 * - 날짜별 카운트 표시
 * - 오늘 하이라이트
 * - 이전/다음 달 이동
 * - 컴팩트/일반 모드
 */

import { useMemo, useCallback } from 'react';
import dayjs from 'dayjs';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { UnifiedCalendarProps, DateRange, DateCount } from './types';
import { WEEKDAYS_KO, CALENDAR_STYLES } from './constants';
import { useCalendarState } from './useCalendarState';

export function UnifiedCalendar({
    mode = 'single',
    selected,
    selectedRange,
    selectedDates,
    onSelect,
    onRangeSelect,
    onMultipleSelect,
    onMonthChange,
    dateCounts = {},
    disabledDates,
    minDate,
    maxDate,
    weekStartsOn = 0,
    showToday = true,
    numberOfMonths = 1,
    compact = false,
    disabled = false,
    className,
}: UnifiedCalendarProps) {
    const { state, actions, utils } = useCalendarState({
        initialDate: selected,
        initialRange: selectedRange,
        mode,
    });

    // 캘린더 그리드 생성
    const calendarGrids = useMemo(() => {
        const grids: { month: Date; weeks: Date[][] }[] = [];
        let currentMonth = dayjs(state.currentMonth);

        for (let i = 0; i < numberOfMonths; i++) {
            grids.push({
                month: currentMonth.toDate(),
                weeks: utils.getCalendarGrid(currentMonth.toDate()),
            });
            currentMonth = currentMonth.add(1, 'month');
        }

        return grids;
    }, [state.currentMonth, numberOfMonths, utils]);

    // 날짜 비활성화 확인
    const isDateDisabled = useCallback(
        (date: Date): boolean => {
            if (disabled) return true;
            if (utils.isDisabled(date, minDate, maxDate)) return true;
            if (Array.isArray(disabledDates)) {
                return disabledDates.some((d) => dayjs(d).isSame(date, 'day'));
            }
            if (typeof disabledDates === 'function') {
                return disabledDates(date);
            }
            return false;
        },
        [disabled, minDate, maxDate, disabledDates, utils]
    );

    // 날짜 선택 여부
    const isDateSelected = useCallback(
        (date: Date): boolean => {
            if (mode === 'single' && selected) {
                return dayjs(date).isSame(selected, 'day');
            }
            if (mode === 'range' && selectedRange?.from) {
                if (dayjs(date).isSame(selectedRange.from, 'day')) return true;
                if (selectedRange.to && dayjs(date).isSame(selectedRange.to, 'day')) return true;
            }
            if (mode === 'multiple' && selectedDates) {
                return selectedDates.some((d) => dayjs(d).isSame(date, 'day'));
            }
            return false;
        },
        [mode, selected, selectedRange, selectedDates]
    );

    // 범위 내 날짜 여부
    const isInSelectedRange = useCallback(
        (date: Date): boolean => {
            if (mode !== 'range' || !selectedRange?.from || !selectedRange?.to) return false;
            const d = dayjs(date);
            return d.isAfter(selectedRange.from, 'day') && d.isBefore(selectedRange.to, 'day');
        },
        [mode, selectedRange]
    );

    // 날짜 클릭 핸들러
    const handleDateClick = useCallback(
        (date: Date) => {
            if (isDateDisabled(date)) return;

            if (mode === 'single') {
                onSelect?.(date);
            } else if (mode === 'range') {
                if (!selectedRange?.from || selectedRange.to) {
                    // 새 범위 시작
                    onRangeSelect?.({ from: date, to: undefined });
                } else {
                    // 범위 완료
                    const from = selectedRange.from;
                    if (dayjs(date).isBefore(from, 'day')) {
                        onRangeSelect?.({ from: date, to: from });
                    } else {
                        onRangeSelect?.({ from, to: date });
                    }
                }
            } else if (mode === 'multiple') {
                const currentDates = selectedDates || [];
                const exists = currentDates.some((d) => dayjs(d).isSame(date, 'day'));
                if (exists) {
                    onMultipleSelect?.(
                        currentDates.filter((d) => !dayjs(d).isSame(date, 'day'))
                    );
                } else {
                    onMultipleSelect?.([...currentDates, date]);
                }
            }
        },
        [mode, selectedRange, selectedDates, onSelect, onRangeSelect, onMultipleSelect, isDateDisabled]
    );

    // 월 변경 핸들러
    const handleMonthChange = useCallback(
        (direction: 'prev' | 'next') => {
            const newMonth =
                direction === 'prev'
                    ? dayjs(state.currentMonth).subtract(1, 'month').toDate()
                    : dayjs(state.currentMonth).add(1, 'month').toDate();
            actions.setMonth(newMonth);
            onMonthChange?.(newMonth);
        },
        [state.currentMonth, actions, onMonthChange]
    );

    // 날짜 카운트 가져오기
    const getDateCount = (date: Date): DateCount | undefined => {
        const dateStr = dayjs(date).format('YYYY-MM-DD');
        return dateCounts[dateStr];
    };

    // 날짜 셀 렌더링
    const renderDateCell = (date: Date, monthDate: Date) => {
        const isCurrentMonth = dayjs(date).isSame(monthDate, 'month');
        const isToday = utils.isToday(date);
        const isSelected = isDateSelected(date);
        const isInRange = isInSelectedRange(date);
        const isDisabled = isDateDisabled(date);
        const isRangeStart = mode === 'range' && selectedRange?.from && dayjs(date).isSame(selectedRange.from, 'day');
        const isRangeEnd = mode === 'range' && selectedRange?.to && dayjs(date).isSame(selectedRange.to, 'day');
        const dayOfWeek = dayjs(date).day();
        const count = getDateCount(date);

        const dateStr = dayjs(date).format('YYYY-MM-DD');

        return (
            <div
                key={dateStr}
                onClick={() => handleDateClick(date)}
                className={cn(
                    'relative p-1 transition-all cursor-pointer',
                    compact ? 'min-h-[40px]' : 'min-h-[60px]',
                    // 현재 월 여부
                    !isCurrentMonth && 'opacity-30',
                    // 비활성화
                    isDisabled && 'cursor-not-allowed opacity-50',
                    // 범위 선택 배경
                    isInRange && 'bg-accent',
                    isRangeStart && 'rounded-l-md bg-primary/10',
                    isRangeEnd && 'rounded-r-md bg-primary/10',
                    // 호버 효과
                    !isDisabled && 'hover:bg-accent/50'
                )}
            >
                {/* 날짜 숫자 */}
                <div
                    className={cn(
                        'flex items-center justify-center rounded-full transition-all',
                        compact ? 'w-7 h-7 text-sm' : 'w-8 h-8 text-sm',
                        // 오늘
                        showToday && isToday && !isSelected && 'bg-primary/20 text-primary font-bold',
                        // 선택됨
                        isSelected && 'bg-primary text-primary-foreground font-bold',
                        // 주말 색상
                        !isSelected && dayOfWeek === 0 && 'text-destructive',
                        !isSelected && dayOfWeek === 6 && 'text-info'
                    )}
                >
                    {dayjs(date).date()}
                </div>

                {/* 날짜별 카운트 (컴팩트 모드가 아닐 때) */}
                {!compact && count && count.total && count.total > 0 && (
                    <div className="mt-1 space-y-0.5">
                        <div className="text-[10px] text-muted-foreground text-center">
                            {count.total}건
                        </div>
                        {count.pending && count.pending > 0 && (
                            <Badge
                                variant="secondary"
                                className="text-[8px] px-1 py-0 bg-warning/15 text-warning block text-center"
                            >
                                대기 {count.pending}
                            </Badge>
                        )}
                    </div>
                )}

                {/* 컴팩트 모드 카운트 */}
                {compact && count && count.total && count.total > 0 && (
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2">
                        <div className="flex gap-0.5">
                            {Array.from({ length: Math.min(count.total, 3) }).map((_, i) => (
                                <div key={i} className="w-1 h-1 rounded-full bg-primary" />
                            ))}
                            {count.total > 3 && (
                                <span className="text-[8px] text-muted-foreground">+</span>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className={cn('space-y-4', className)}>
            {/* 월 네비게이션 */}
            <div className="flex items-center justify-between">
                <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleMonthChange('prev')}
                    disabled={disabled}
                    className="h-8 w-8"
                >
                    <ChevronLeft className="h-4 w-4" />
                </Button>

                <div className="flex gap-4">
                    {calendarGrids.map((grid, idx) => (
                        <h2
                            key={idx}
                            className="text-lg font-bold min-w-[120px] text-center"
                        >
                            {dayjs(grid.month).format('YYYY년 M월')}
                        </h2>
                    ))}
                </div>

                <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleMonthChange('next')}
                    disabled={disabled}
                    className="h-8 w-8"
                >
                    <ChevronRight className="h-4 w-4" />
                </Button>
            </div>

            {/* 캘린더 그리드 */}
            <div className={cn('flex gap-8', numberOfMonths > 1 && 'flex-row')}>
                {calendarGrids.map((grid, gridIdx) => (
                    <div key={gridIdx} className="flex-1">
                        {/* 요일 헤더 */}
                        <div className="grid grid-cols-7 mb-2">
                            {WEEKDAYS_KO.map((day, idx) => (
                                <div
                                    key={day}
                                    className={cn(
                                        'text-center text-xs font-medium py-2',
                                        idx === 0 && 'text-destructive',
                                        idx === 6 && 'text-info',
                                        idx !== 0 && idx !== 6 && 'text-muted-foreground'
                                    )}
                                >
                                    {day}
                                </div>
                            ))}
                        </div>

                        {/* 날짜 그리드 */}
                        <div className="border rounded-lg overflow-hidden">
                            {grid.weeks.map((week, weekIdx) => (
                                <div key={weekIdx} className="grid grid-cols-7 border-b last:border-b-0">
                                    {week.map((date) => renderDateCell(date, grid.month))}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {/* 범례 (카운트가 있을 때) */}
            {Object.keys(dateCounts).length > 0 && !compact && (
                <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground pt-2 border-t">
                    {showToday && (
                        <div className="flex items-center gap-1">
                            <div className="w-3 h-3 rounded-full bg-primary/20 ring-1 ring-primary" />
                            <span>오늘</span>
                        </div>
                    )}
                    <div className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded-full bg-primary" />
                        <span>선택됨</span>
                    </div>
                </div>
            )}
        </div>
    );
}

export default UnifiedCalendar;
